import { useContext, useEffect, useState } from 'react';
import { HighchartsReactRefObject } from 'highcharts-react-official';

import AccountContext from 'contexts/AccountContext';
import DashboardContextMenuContext from 'contexts/DashboardContextMenuContext';
import useV5ChartData from 'hooks/useV5ChartData';
import useChartDrillDowns from 'hooks/useChartDrillDowns';
import useMetric from 'hooks/useMetric';
import useValueFormatters from 'hooks/useValueFormatters';
import metricTypeCheckers from 'types/metricTypeCheckers';
import pie from '../../highchartOptions/pieOptions';
import toBreakdownRows from '../../toBreakdownRows';

interface PieSeriesItem {
  name: string;
  data: { name: string; y: number }[];
  prefix?: PreOrPostFix;
  postfix?: PreOrPostFix;
}

// One series of each part's share of the total, largest first. Sorted as
// a copy: the response is the chart data's own.
export const getSeriesFor = (
  data: V5ChartData,
  chartDef: V5ChartDefinition,
): PieSeriesItem[] => {
  const firstOption = Object.values(data)[0];
  if (!firstOption) {
    return [];
  }
  const { metric } = firstOption;
  const metricIds = metricTypeCheckers.isCompoundMetric(metric)
    ? metric.metricIds
    : [metric.id];
  // Without rows, an empty series: the chart has no data to display
  const rows = toBreakdownRows({ data, chartDef });
  const total = rows.reduce((sum, row) => sum + row.value, 0);
  const shares = rows
    .map((row) => ({ name: row.name, y: (row.value / total) * 100 }))
    .sort((a, b) => (a.y > b.y ? -1 : a.y === b.y ? 0 : 1));
  return [
    {
      name: metric.name,
      data: shares,
      metricIds,
      animation: false,
    } as any,
  ];
};

const usePieData = ({
  chartDefinition,
  chartRef,
  innerSize,
}: {
  chartDefinition: V5ChartDefinition;
  chartRef: React.MutableRefObject<HighchartsReactRefObject | null>;
  innerSize?: string;
}): {
  options: Highcharts.Options | undefined;
  isNoData: boolean;
  isReady: boolean;
} => {
  const { unitsLocale } = useContext(AccountContext);
  const { openMenu } = useContext(DashboardContextMenuContext);

  const [groupField] = useState(() => {
    if (chartDefinition.dimensionA) {
      return chartDefinition.dimensionA.field;
    }
    return 'date';
  });
  const [options, setOptions] = useState<Highcharts.Options | undefined>();
  const [isNoDataToDisplay, setIsNoDataToDisplay] = useState<boolean>(false);

  const { data, isLoading } = useV5ChartData(chartDefinition);
  const { formatMetric } = useValueFormatters();
  const metric = useMetric(chartDefinition.series[0].metricId);
  const { onDrillDown, syncChart } = useChartDrillDowns(
    groupField,
    chartRef,
    'pie',
    chartDefinition,
  );

  useEffect(() => {
    if (!metric) {
      return;
    }

    const newOptions = {
      ...pie({
        formatMetric,
        title: ' ',
        precision: metric.formatting.precision,
        openMenu,
        groupField,
        fieldType: chartDefinition.dimensionA
          ? chartDefinition.dimensionA.fieldType
          : undefined,
        chartDef: chartDefinition,
        fontSize: 11,
        // A pie has a hole (pieOptions) unless it's drawn solid
        innerSize: innerSize ?? (chartDefinition.isSolid ? '0%' : undefined),
      }),
    };

    setOptions(newOptions as any as Highcharts.Options);
  }, [
    chartDefinition,
    chartRef,
    formatMetric,
    groupField,
    innerSize,
    metric,
    onDrillDown,
    openMenu,
    syncChart,
    unitsLocale,
  ]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const series = getSeriesFor(data, chartDefinition).map((s) => ({
      ...s,
      type: 'pie' as 'pie',
      onDrillDown,
    }));
    const isNoData =
      series.length === 1 &&
      (series[0].data.length === 0 || !series[0].data.some((d) => !isNaN(d.y)));

    setIsNoDataToDisplay(isNoData);
    setOptions((current) => ({
      ...current,
      series,
    }));
  }, [chartDefinition, chartRef, data, onDrillDown]);

  useEffect(() => {
    syncChart();
  }, [syncChart, onDrillDown, data]);

  return {
    options,
    isNoData: isNoDataToDisplay,
    isReady: !!data && !!metric && !isLoading,
  };
};

export default usePieData;
